import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Plus, Upload, Pencil, Phone, PhoneIncoming, PhoneOutgoing, Clock, Trash2, Download, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useAgents, useDeleteAgent, useCreateAgent, type AgentListItem } from "@/services/assistant.query";
import { DataPagination } from "@/components/ui/data-pagination";
import { emptyPage, useListParams } from "@/lib/list-query";
import { getAssistant } from "@/services/assistant.service";
import {
  type AgentTool,
  createCustomTool,
  createNativeTool,
  isNativeTool,
  listNativeTools,
  updateNativeTool,
} from "@/services/tool.service";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import { formatDistanceToNow } from "date-fns";
import { downloadAgentConfig, importedAgentConfig } from "@/utils/agent-export";
import { toast } from "sonner";
import { voiceDisplayName } from "@/constants/geminiVoices";
import { GEMINI_3_8_LIVE } from "@/constants/liveModels";
import { KB_MIN_SIMILARITY_DEFAULT } from "@/utils/kb-match-strictness";
import { KB_TOP_K_DEFAULT } from "@/utils/kb-results-per-search";
import { CreateAgentDialog } from "@/components/Agents/CreateAgentDialog";
import { validateParameterSchema } from "@/utils/tool-name-utils";
import { importedHeaders, storedOptionColumns } from "@/utils/tool-options";
import { ApiError } from "@/lib/api-client";

export default function Agents() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const { currentWorkspaceId } = useWorkspace();
  const { canEdit, canDelete } = useWorkspacePermissions();
  // Searching, paging and sorting all happen server-side; see lib/list-query.
  const list = useListParams();
  const { data, isLoading, isFetching } = useAgents(currentWorkspaceId || undefined, list.params);
  const page = data ?? emptyPage<AgentListItem>();
  // Deleting the last agent on the last page leaves `params.page` past the end.
  const { clampToTotal } = list;
  useEffect(() => {
    if (!isLoading) clampToTotal(page.total);
  }, [isLoading, page.total, clampToTotal]);
  const deleteAgent = useDeleteAgent();
  const createAgent = useCreateAgent(currentWorkspaceId || "");

  // The list row carries only a trimmed config (see AgentListItem), so fetch
  // the whole agent before exporting it — otherwise the file ships empty.
  const handleExportClick = async (e: React.MouseEvent, agent: { id: string }) => {
    e.stopPropagation();
    try {
      await downloadAgentConfig(await getAssistant(agent.id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to export agent");
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentWorkspaceId) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      if (!importData.config || !importData.agent_name) {
        throw new Error("Invalid agent configuration file");
      }

      // Validate all tool schemas BEFORE creating anything, so a bad file
      // leaves no agent behind
      if (importData.version === "2.0" && importData.tools && Array.isArray(importData.tools)) {
        for (const tool of importData.tools) {
          if (tool.parameters) {
            const schemaError = validateParameterSchema(tool.parameters);
            if (schemaError) {
              toast.error(`Import blocked: Tool "${tool.tool_name}" has invalid schema. ${schemaError}`);
              return;
            }
          }
        }
      }

      // Clean config - remove any external resource references
      const cleanConfig = importedAgentConfig(importData.config);

      // Create the agent first (without tools)
      const newAgent = await createAgent.mutateAsync({
        agent_name: `${importData.agent_name} (Imported)`,
        agent_type: importData.agent_type || "Single Prompt",
        config: cleanConfig,
      });

      // Create independent copies of tools if included in export (v2.0+ format)
      let toolCount = 0;
      const failedTools: string[] = [];
      // Created without the header values the file holds only as the saved-secret
      // placeholder: "<tool> (<Header>, …)", for the user to type again.
      const reenter: string[] = [];
      if (importData.version === "2.0" && importData.tools && Array.isArray(importData.tools)) {
        // One at a time: each create also appends its id to the agent's
        // toolIds, and concurrent appends would lose ids. Each kind goes to its
        // own endpoint with its own exported columns. Any refusal is reported.
        for (const tool of importData.tools as AgentTool[]) {
          try {
            if (isNativeTool(tool)) {
              // A blank description is left out: the create then writes the
              // default, and an update keeps the row's own.
              const settings = {
                description: tool.description || undefined,
                timeout_seconds: tool.timeout_seconds,
                sms_content_type: tool.sms_content_type,
                sms_static_message: tool.sms_static_message,
                transfer_call_mode: tool.transfer_call_mode,
                transfer_call_destination: tool.transfer_call_destination,
                transfer_call_type: tool.transfer_call_type,
              };
              try {
                await createNativeTool({ agent_id: newAgent.id, tool_name: tool.tool_name, ...settings });
              } catch (error) {
                // The agent create already provisioned this native from the
                // file's config, which can lag the row: the row's settings win.
                if (!(error instanceof ApiError && error.status === 409)) throw error;
                const row = (await listNativeTools(newAgent.id)).find((t) => t.tool_name === tool.tool_name);
                if (!row) throw error;
                await updateNativeTool(row.id, settings);
              }
            } else {
              const { headers, masked } = importedHeaders(tool.headers);
              await createCustomTool({
                agent_id: newAgent.id,
                tool_name: tool.tool_name,
                description: tool.description,
                endpoint_url: tool.endpoint_url,
                http_method: tool.http_method,
                headers,
                parameters: tool.parameters,
                timeout_seconds: tool.timeout_seconds,
                // Normalised, so a bad value in the file never drops the tool.
                ...storedOptionColumns(tool),
              });
              if (masked.length > 0) reenter.push(`${tool.tool_name} (${masked.join(", ")})`);
            }
            toolCount++;
          } catch (error) {
            console.error("Failed to create tool:", tool.tool_name, error);
            failedTools.push(tool.tool_name);
          }
        }
      } else if (!importData.version || importData.version === "1.0") {
        // Legacy export format - tools not included, just log info
        console.info("Legacy export format detected (v1.0) - tools not included in export");
      }

      const toolMessage = toolCount > 0 
        ? ` with ${toolCount} tool${toolCount > 1 ? 's' : ''}`
        : '';
      const reenterMessage = reenter.length > 0
        ? `. Re-enter the saved header values of: ${reenter.join(", ")} — exports never include secrets.`
        : "";
      // A list of values to type again stays on screen until it is dismissed.
      const untilDismissed = { duration: Infinity, closeButton: true };
      if (failedTools.length > 0) {
        const failed = `Agent "${importData.agent_name}" imported${toolMessage}, but ${failedTools.length} tool${failedTools.length > 1 ? "s" : ""} could not be created: ${failedTools.join(", ")}`;
        if (reenterMessage) toast.warning(`${failed}${reenterMessage}`, untilDismissed);
        else toast.warning(failed);
      } else if (reenterMessage) {
        toast.warning(`Agent "${importData.agent_name}" imported${toolMessage}${reenterMessage}`, untilDismissed);
      } else {
        toast.success(`Agent "${importData.agent_name}" imported successfully${toolMessage}`);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import agent");
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    setAgentToDelete(agentId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (agentToDelete) {
      // A refusal — a campaign still using the agent — is already a toast
      // (useDeleteAgent's onError); either way the confirm is done with.
      deleteAgent.mutate(agentToDelete, {
        onSettled: () => {
          setDeleteDialogOpen(false);
          setAgentToDelete(null);
        },
      });
    }
  };

  const handleCreateAgent = async (name: string) => {
    if (!currentWorkspaceId) {
      toast.error("No workspace selected");
      return;
    }

    try {
      const newAgent = await createAgent.mutateAsync({
        agent_name: name,
        agent_type: "Single Prompt",
        config: {
          systemPrompt: "",
          llm: {
            provider: "google",
            model: GEMINI_3_8_LIVE,
            temperature: 0.7,
          },
          voice: {
            voiceId: "Charon",
          },
          toolIds: [],
          kbMinSimilarity: KB_MIN_SIMILARITY_DEFAULT,
          kbTopK: KB_TOP_K_DEFAULT,
          nativeTools: {},
          settings: {
            initialMessage: { enabled: true },
            postCallAnalysis: { enabled: false, fields: [] },
            endCallOnSilence: true,
            maxCallDuration: 10,
            silenceTimeoutMinutes: 2,
            voicemailDetection: true,
            webhookUrl: "",
          },
        },
      });

      toast.success("Agent created successfully");
      setCreateDialogOpen(false);
      navigate(`/${currentWorkspaceId}/agents/${newAgent.id}?new=true`);
    } catch (error) {
      console.error("Failed to create agent:", error);
      toast.error("Failed to create agent");
    }
  };

  const isPending = createAgent.isPending || isImporting;

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-background to-muted/30">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm shadow-sm">
        <div className="flex items-center justify-between px-8 py-5">
          <div className="flex items-center gap-4 flex-1 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search agents..."
                className="pl-10 bg-background shadow-sm"
                value={list.searchInput}
                onChange={(e) => list.setSearchInput(e.target.value)}
              />
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-3">
              <input
                type="file"
                data-testid="agent-import-input"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".json"
              />
              <Button
                variant="outline"
                size="sm"
                className="shadow-sm"
                onClick={handleImportClick}
                disabled={isPending}
              >
                {isImporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {isImporting ? "Importing..." : "Import"}
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
                className="shadow-md hover:shadow-lg"
                disabled={isPending}
              >
                {createAgent.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create an Agent
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent mb-2">All Agents</h1>
          <p className="text-base text-muted-foreground">
            Manage and monitor your AI voice agents
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-24" role="status" aria-label="Loading agents">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : page.rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {list.params.search
              ? "No agents found matching your search."
              : "No agents yet. Create your first agent to get started."}
          </div>
        ) : (
          /* `relative` so the spinner can sit over the page being replaced.
             Paging keeps the previous rows on screen rather than blanking the
             grid (see `keepPreviousData`), so dimming them under a centred
             spinner is what tells the user the page is on its way. */
          <div className="relative" aria-busy={isFetching}>
            {isFetching && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-background/60"
                role="status"
                aria-label="Loading agents"
              >
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            <div
              className={cn(
                "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
                isFetching && "pointer-events-none",
              )}
            >
            {page.rows.map((agent) => (
              <Card
                key={agent.id}
                data-testid="agent-card"
                className="group cursor-pointer hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border-2 hover:border-primary/50 overflow-hidden"
                onClick={() => navigate(currentWorkspaceId ? `/${currentWorkspaceId}/agents/${agent.id}` : `/agents/${agent.id}`)}
              >
                <CardHeader className="pb-3 bg-gradient-to-br from-primary/5 to-transparent">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors truncate">
                        {agent.agent_name}
                      </h3>
                      {agent.config?.voice && (
                        <Badge variant="secondary" className="text-xs font-medium shadow-sm">
                          {typeof agent.config.voice === 'string' 
                            ? voiceDisplayName(agent.config.voice)
                            : voiceDisplayName(agent.config.voice.voiceId)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all shrink-0">
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:bg-primary hover:text-primary-foreground border-primary/30"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(currentWorkspaceId ? `/${currentWorkspaceId}/agents/${agent.id}` : `/agents/${agent.id}`);
                          }}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="hover:bg-primary/10 hover:text-primary border-primary/30"
                        onClick={(e) => handleExportClick(e, agent)}
                        title="Export Agent Configuration"
                        data-testid="agent-export"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="hover:bg-destructive hover:text-destructive-foreground border-destructive/30 text-destructive"
                          onClick={(e) => handleDeleteClick(e, agent.id)}
                          data-testid="agent-delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {!agent.inbound_phone_number && !agent.outbound_phone_number ? (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 ring-2 ring-primary/20">
                        <Phone className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-muted-foreground">No phone assigned</span>
                    </div>
                  ) : (
                    <>
                      {agent.inbound_phone_number && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 ring-2 ring-primary/20">
                            <PhoneIncoming className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">Inbound:</span>{" "}
                            {agent.inbound_phone_number}
                          </span>
                        </div>
                      )}
                      {agent.outbound_phone_number && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 ring-2 ring-primary/20">
                            <PhoneOutgoing className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">Outbound:</span>{" "}
                            {agent.outbound_phone_number}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/20 ring-2 ring-purple-200 dark:ring-purple-800/30">
                      <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <span className="text-muted-foreground">
                      {agent.updated_at
                        ? `Updated ${formatDistanceToNow(new Date(agent.updated_at), {
                            addSuffix: true,
                          })}`
                        : "Just created"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          </div>
        )}

        {page.total > 0 && (
          <DataPagination
            page={list.params.page}
            size={list.params.size}
            total={page.total}
            onPageChange={list.setPage}
            onSizeChange={list.setSize}
            label="agents"
          />
        )}
      </div>

      <CreateAgentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateAgent}
        isCreating={createAgent.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this agent? This action cannot be undone. Its calls, campaigns and knowledge bases are kept; its tools and Cal.com connection are deleted with it. Phone numbers that used it will have no agent until you assign one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
